import { type FormEvent, type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type PayoutContext, type PayoutRow, type PayoutStatus } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import {
  maySelfApprove,
  PAYOUT_QUEUE_STATUSES,
  decidePayout,
  markPayoutFailed,
  markPayoutPaid,
  payoutCapMinor,
  payoutSettleAuthority,
  payoutSettleable,
  payoutWithinCap,
  formatMarketInstant,
  issueDriverPayout,
  loadPayoutContext,
  loadPayoutQueue,
  marketDisplayName,
  marketTimeZone,
  payoutActor,
  payoutApprovalBlock,
  payoutAuthority,
  payoutRejectable,
  payoutStatusTone,
  payoutIssueAttempt,
  summarizePayoutQueue,
  type PayoutIssueAttempt,
} from '../../src/index';

/** D412: a fresh idempotency key for a new issue attempt (never a counter: keys outlive a page reload on the server). */
function newIssueKey(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
import { SelfApprovalNote } from '../components/self-approval-note';
import { useAdmin } from '../session';
import { PersonName } from '../components/person-name';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Field, MoneyInput, Notice, PageHeader, Panel, Select, StatCard, Table, TextInput, FormRow, EmptyState, SectionTitle, Divider, Grid, NoteText, PanelBody, Row, Stack, Text } from '@vello/web-theme';

/**
 * Repasses (D318; four eyes D385, ADR-0055) — the operator payout desk over the real S094 money surface.
 *
 * - The per-market **queue** (`GET /v1/payouts/queue`, awaiting approval by default) with exact per-currency totals
 *   (bigint, never summed across currencies), every status and decision **translated** (never a raw enum), and for each
 *   payout **who issued it and who decided it** (the sign-in name when the server resolves it, "you" for oneself).
 * - The **decision** (`POST /v1/payouts/:id/decision`, `payout:approve`) with a mandatory reason. Approve is **hidden on
 *   one's own payout, with the reason shown** — whoever issues cannot approve; the server refuses `SELF_APPROVAL` anyway,
 *   whatever the grant. The maker may still withdraw (reject) its own payout. A payout issued before D385 has no recorded
 *   issuer and can never be decided; it says so instead of offering buttons.
 * - The **issue** form (`POST /v1/payouts`, `payout:create`, the screen's one primary action): the driver is checked
 *   first (`GET /v1/payouts/context`) so the amount is typed in the currency of the driver's market through the design
 *   system's `MoneyInput` — never minor units, never a typed currency code.
 * - W6-EARNINGS-LEDGER A2 (D392): **what the ledger owes** is shown wherever money is decided — the driver check
 *   states the payable, the cash he is still holding, what is already requested and the cap a new payout may draw; the
 *   queue carries the same position per row, so a checker approving knows whether the ledger carries it. The desk only
 *   SHOWS it: the server caps every issue and every approval against the book itself.
 * - A2: the **settlement rail** on an approved payout — mark it paid with the rail's own transfer reference (which
 *   moves the reservation out of `platform:payout_pending`), or mark it not paid with a reason (which gives the money
 *   back to the payee). Both are `payout:settle`.
 */
export function PayoutsScreen(): ReactElement {
  const { runtime, markets, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { v, fail, explain } = useVocab();
  const operatorId = me?.id;
  const { mayIssue, mayDecide } = payoutAuthority(grants);
  const maySettle = payoutSettleAuthority(grants);
  // Owner 2026-10-12: the market of the queue IS the header scope's city — no second market selector on this screen.
  const { market: scopeMarket, choices } = useScopeMarket();
  const market = scopeMarket ?? '';
  const [status, setStatus] = useState<PayoutStatus>('REQUESTED');
  const [loaded, setLoaded] = useState<{ market: string; status: PayoutStatus } | undefined>(undefined);
  const [rows, setRows] = useState<readonly PayoutRow[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger' | 'warning'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const readSeq = useRef(0);

  const [driverId, setDriverId] = useState('');
  const [context, setContext] = useState<PayoutContext | undefined>(undefined);
  const [checking, setChecking] = useState(false);
  const [amount, setAmount] = useState<bigint | undefined>(undefined);
  const [issueMsg, setIssueMsg] = useState<{ tone: 'success' | 'danger' | 'warning'; text: string } | undefined>(undefined);
  const [issueBusy, setIssueBusy] = useState(false);
  /** D412: the issue attempt in flight (its idempotency key), kept until the server answers it. */
  const [issueAttempt, setIssueAttempt] = useState<PayoutIssueAttempt | undefined>(undefined);
  // A2 (D392): the settlement rail — one reference typed once, used by whichever row the operator acts on.
  const [externalRef, setExternalRef] = useState('');

  const money = useCallback(
    (m: { readonly amountMinor: string; readonly currency: string }): string =>
      /^-?\d+$/.test(m.amountMinor) && isIso4217Currency(m.currency) ? formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' }) : `${m.amountMinor} ${m.currency}`,
    [lang],
  );
  const statusLabel = (s: string): string => v('payout.status', s);
  const refusal = (e: { reasonCode?: string; detail?: string; code: string }, fallbackKey: string): string => {
    return explain(e) ?? t(fallbackKey, { code: fail(e) });
  };

  const load = useCallback(
    async (m: string, s: PayoutStatus): Promise<void> => {
      const seq = ++readSeq.current;
      setLoaded((prev) => (prev !== undefined && prev.market === m && prev.status === s ? prev : { market: m, status: s }));
      if (m === '') {
        setError(undefined);
        setRows([]);
        return; // an un-scoped read is fail-closed empty on the server: ask for a market instead of pretending
      }
      const res = await loadPayoutQueue(runtime, m, s);
      if (seq !== readSeq.current) return;
      if (res.ok) {
        setRows(res.value.payouts);
        setError(undefined);
      } else {
        setRows([]);
        setError(t('pay.error.queue', { code: res.error.code }));
      }
    },
    [runtime, t],
  );

  // The queue follows the header scope's market and the status chosen, and stays current by itself (no "load" button).
  useEffect(() => {
    void load(market.trim(), status);
  }, [market, status, load]);
  useAutoRefresh(() => load(market.trim(), status), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market.trim() !== '' });

  const decide = async (row: PayoutRow, decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    setBusy(true);
    setMsg(undefined);
    const res = await decidePayout(runtime, row.payoutId, { decision, reason: reason.trim(), ...(loaded !== undefined && loaded.market !== '' ? { market: loaded.market } : {}) });
    setBusy(false);
    if (res.ok) {
      const own = row.requestedBy !== undefined && row.requestedBy !== null && row.requestedBy === operatorId;
      setMsg({ tone: 'success', text: t(decision === 'APPROVE' ? 'pay.approved' : own ? 'payouts.withdrawn' : 'pay.rejected', { id: row.payoutId, amount: money(row) }) });
      setReason('');
      if (loaded !== undefined) await load(loaded.market, loaded.status);
      return;
    }
    const code = res.error.reasonCode ?? res.error.detail;
    setMsg({ tone: code === 'FINANCIAL_LIMIT_EXCEEDED' ? 'warning' : 'danger', text: refusal(res.error, 'ui.actionFailed') });
    if ((code === 'PAYOUT_NOT_PENDING' || code === 'PAYOUT_ALREADY_DECIDED') && loaded !== undefined) await load(loaded.market, loaded.status);
  };

  /**
   * D470 (ADR-0088): the operator who pays an approved payout opens its destination's sealed details (`payout:settle`;
   * the server records every open). They stay on screen only for this row until the queue is reloaded.
   */
  const [revealed, setRevealed] = useState<Readonly<Record<string, Readonly<Record<string, string>>>>>({});
  const reveal = async (row: PayoutRow): Promise<void> => {
    if (loaded === undefined || loaded.market === '') return;
    setBusy(true);
    const res = await runtime.finance.revealPayoutDestination(row.payoutId, loaded.market);
    setBusy(false);
    if (res.ok) setRevealed({ ...revealed, [row.payoutId]: res.value.details });
    else setMsg({ tone: 'danger', text: refusal(res.error, 'ui.actionFailed') });
  };

  /** A2: record the transfer's outcome. Paid needs the rail's reference; not-paid needs the same reason field. */
  const settle = async (row: PayoutRow, outcome: 'PAID' | 'FAILED'): Promise<void> => {
    setBusy(true);
    setMsg(undefined);
    const market = loaded !== undefined && loaded.market !== '' ? { market: loaded.market } : {};
    const res = outcome === 'PAID'
      ? await markPayoutPaid(runtime, row.payoutId, { externalRef, ...market })
      : await markPayoutFailed(runtime, row.payoutId, { reason, ...market });
    setBusy(false);
    if (res.ok) {
      setMsg({ tone: 'success', text: outcome === 'PAID' ? t('payouts.settle.paidOk', { id: row.payoutId, ref: externalRef.trim() }) : t('payouts.settle.failedOk', { id: row.payoutId }) });
      setExternalRef('');
      setReason('');
      if (loaded !== undefined) await load(loaded.market, loaded.status);
      return;
    }
    setMsg({ tone: 'danger', text: refusal(res.error, 'ui.actionFailed') });
    if (loaded !== undefined) await load(loaded.market, loaded.status);
  };

  const checkDriver = async (): Promise<void> => {
    const id = driverId.trim();
    if (id === '') return;
    setChecking(true);
    setIssueMsg(undefined);
    setContext(undefined);
    setAmount(undefined);
    const res = await loadPayoutContext(runtime, id);
    setChecking(false);
    if (res.ok) setContext(res.value);
    else setIssueMsg({ tone: 'danger', text: refusal(res.error, 'payouts.issue.failed') });
  };

  const onIssue = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (context === undefined) {
      await checkDriver(); // Enter in the driver field checks the driver first
      return;
    }
    if (context.currency === null || amount === undefined || amount <= 0n) return;
    setIssueBusy(true);
    setIssueMsg(undefined);
    // D412: the same request keeps its attempt's key, so pressing "issue" again after a lost answer replays the payout
    // the server already wrote; a changed driver or amount is a new attempt with a new key.
    const attempt = payoutIssueAttempt(issueAttempt, { driverId: context.driverId, amountMinor: amount, currency: context.currency }, newIssueKey);
    setIssueAttempt(attempt);
    const res = await issueDriverPayout(runtime, { driverId: context.driverId, amountMinor: amount, currency: context.currency, idempotencyKey: attempt.key });
    setIssueBusy(false);
    if (!res.ok) {
      // A reused key never recovers by retrying: drop it so the next press is a new attempt.
      if ((res.error.reasonCode ?? res.error.detail) === 'IDEMPOTENCY_KEY_REUSED') setIssueAttempt(undefined);
      setIssueMsg({ tone: (res.error.reasonCode ?? res.error.detail) === 'FINANCIAL_LIMIT_EXCEEDED' ? 'warning' : 'danger', text: refusal(res.error, 'payouts.issue.failed') });
      return;
    }
    setIssueAttempt(undefined); // answered: the next issue is a new payout
    setIssueMsg({ tone: 'success', text: t(res.value.replayed === true ? 'payouts.issue.replayed' : 'payouts.issue.ok', { id: res.value.payoutId, amount: money(res.value) }) });
    setAmount(undefined);
    // Show the maker the payout it just issued, in the scoped market's queue (where Approve is — rightly — not offered to it).
    setStatus('REQUESTED');
    await load(market.trim(), 'REQUESTED');
  };

  const cap = payoutCapMinor(context?.owed);
  const overCap = !payoutWithinCap(amount, cap);
  /** A2: the payee's ledger position in one line — the desk never shows a bare number with no provenance. */
  const owedSummary = (owed: { readonly currency: string; readonly payableMinor: string; readonly receivableMinor: string; readonly reservedMinor: string; readonly availableMinor: string }): string =>
    t('payouts.owed.summary', {
      payable: money({ amountMinor: owed.payableMinor, currency: owed.currency }),
      receivable: money({ amountMinor: owed.receivableMinor, currency: owed.currency }),
      reserved: money({ amountMinor: owed.reservedMinor, currency: owed.currency }),
      available: money({ amountMinor: owed.availableMinor, currency: owed.currency }),
    });

  const totals = summarizePayoutQueue(rows ?? []);
  const hasReason = reason.trim() !== '';
  const tz = marketTimeZone(loaded?.market, markets);
  const when = (instant: string | null | undefined): string => formatMarketInstant(instant ?? undefined, tz, lang);
  const actorName = (id: string | null | undefined, label: string | null | undefined): string | undefined => {
    const a = payoutActor(id, label, operatorId);
    return a.kind === 'you' ? t('payouts.actor.you') : a.kind === 'named' ? a.name : undefined;
  };

  return (
    <>
      <PageHeader title={t('pay.title')} subtitle={t('payouts.subtitle')} />

      <Panel>
        <Stack gap={3}>
          <FormRow columns="minmax(0, 240px)">
            <Field label={t('pay.field.status')} htmlFor="pay-status">
              <Select id="pay-status" value={status} onChange={(e) => setStatus(e.target.value as PayoutStatus)}>
                {PAYOUT_QUEUE_STATUSES.map((s) => (
                  <option key={s} value={s}>{statusLabel(s)}</option>
                ))}
              </Select>
            </Field>
          </FormRow>
        {error !== undefined && <Notice tone="danger">{error}</Notice>}
        </Stack>
      </Panel>

      {scopeMarket === undefined ? (
        <ScopeMarketNotice choices={choices} />
      ) : (
      <>
      {totals.length > 0 && (
        <Grid min="sm">
          {totals.map((tot) => (
            <StatCard key={tot.currency} label={t('pay.kpi.total', { currency: tot.currency })} value={money({ amountMinor: tot.totalMinor, currency: tot.currency })} hint={t('pay.kpi.count', { count: String(tot.count) })} />
          ))}
        </Grid>
      )}

      {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}

      <Panel padded={false}>
        <PanelBody>
        <Grid columns={2}>
          <SectionTitle>
            {t('pay.queue.title', { status: statusLabel(loaded?.status ?? status) })}
          </SectionTitle>
          <Stack gap={3}>
            <Field label={t('ui.reason')} htmlFor="pay-reason" hint={t('pay.reason.hint')}>
              <TextInput id="pay-reason" value={reason} maxLength={500} placeholder={t('ui.reason.placeholder')} onChange={(e) => setReason(e.target.value)} />
            </Field>
            {maySettle && (
              <Field label={t('payouts.settle.ref')} htmlFor="pay-external-ref" hint={t('payouts.settle.refHint')}>
                <TextInput id="pay-external-ref" className="tnum" value={externalRef} maxLength={200} autoComplete="off" spellCheck={false} onChange={(e) => setExternalRef(e.target.value)} />
              </Field>
            )}
          </Stack>
        </Grid>
        </PanelBody>
        <Divider />
        <Table
          columns={[
            { key: 'driver', header: t('pay.col.driver'), render: (p: PayoutRow) => <PersonName id={p.driverId} /> },
            { key: 'amount', header: t('pay.col.amount'), numeric: true, render: (p: PayoutRow) => <span className="tnum">{money(p)}</span> },
            { key: 'status', header: t('pay.col.status'), render: (p: PayoutRow) => <Badge tone={payoutStatusTone(p.status)}>{statusLabel(p.status)}</Badge> },
            {
              key: 'owed',
              header: t('payouts.col.owed'),
              numeric: true,
              render: (p: PayoutRow) =>
                p.owed === undefined || p.owed === null ? (
                  <Text role="caption" tone="tertiary" block>—</Text>
                ) : (
                  <span>
                    <span className="tnum">{money({ amountMinor: p.owed.availableMinor, currency: p.owed.currency })}</span>
                    <Text role="caption" tone="tertiary" block mono>{owedSummary(p.owed)}</Text>
                  </span>
                ),
            },
            {
              key: 'issued',
              header: t('payouts.col.issuedBy'),
              render: (p: PayoutRow) => (
                <span>
                  {/* D470: the driver asked for it himself (his app) — one checker other than him decides it. */}
                  {p.origin === 'DRIVER' ? t('payouts.origin.driver') : (actorName(p.requestedBy, p.requestedByLabel) ?? t('payouts.actor.unrecorded'))}
                  {p.speed === 'INSTANT' && <> <Badge tone="warning">{t('payouts.speed.instant')}</Badge></>}
                  <Text role="caption" tone="tertiary" block mono>{when(p.createdAt)}</Text>
                  {p.destinationLabel !== undefined && p.destinationLabel !== null && <Text role="caption" tone="tertiary" block>{t('payouts.destination.label', { label: p.destinationLabel })}</Text>}
                </span>
              ),
            },
            {
              key: 'decided',
              header: t('payouts.col.decidedBy'),
              render: (p: PayoutRow) => {
                const who = actorName(p.decidedBy, p.decidedByLabel);
                if (who === undefined) return <Text role="caption" tone="tertiary" block>{p.status === 'REQUESTED' ? t('payouts.actor.pending') : t('payouts.actor.unrecorded')}</Text>;
                return (
                  <span>
                    {who}
                    {p.decision !== undefined && p.decision !== null && <Text role="caption" tone="tertiary" block>{t(`payouts.decision.${p.decision}`, { when: when(p.decidedAt) })}</Text>}
                    {p.decisionReason !== undefined && p.decisionReason !== null && <Text role="caption" tone="tertiary" block>{t('payouts.decision.reason', { reason: p.decisionReason })}</Text>}
                    <SelfApprovalNote record={p} />
                  </span>
                );
              },
            },
            {
              key: 'act',
              header: '',
              render: (p: PayoutRow) => {
                if (payoutSettleable(p, maySettle)) {
                  // A2 (D392): the transfer's outcome. Paid needs the rail's reference; not paid needs the reason.
                  const details = revealed[p.payoutId];
                  return (
                    <Stack gap={2} align="end">
                      {p.destinationId !== undefined && p.destinationId !== null && (details === undefined ? (
                        <Button variant="ghost" disabled={busy} onClick={() => void reveal(p)}>{t('payouts.destination.reveal')}</Button>
                      ) : (
                        <Text role="caption" tone="tertiary" block mono data-testid={`payout-destination-${p.payoutId}`}>
                          {Object.entries(details).map(([k, value], i) => <span key={k}>{i > 0 ? ' · ' : ''}<span title={k}>{value}</span></span>)}
                        </Text>
                      ))}
                      <Row gap={2} justify="end">
                        <Button variant="secondary" disabled={busy || externalRef.trim() === ''} onClick={() => void settle(p, 'PAID')}>{t('payouts.settle.paid')}</Button>
                        <Button variant="danger" disabled={busy || !hasReason} onClick={() => void settle(p, 'FAILED')}>{t('payouts.settle.failed')}</Button>
                      </Row>
                    </Stack>
                  );
                }
                if (p.status !== 'REQUESTED') {
                  return (
                    <Text role="caption" tone="tertiary" block className="vello-ab-end">
                      {p.externalRef !== undefined && p.externalRef !== null ? p.externalRef : p.status === 'PROCESSING' && !maySettle ? t('payouts.settle.noPermission') : t('pay.settlementRail')}
                    </Text>
                  );
                }
                const block = payoutApprovalBlock(p, operatorId, mayDecide, selfApproval);
                const own = operatorId !== undefined && p.requestedBy === operatorId;
                return (
                  <Stack gap={2} align="end">
                    <Row gap={2} justify="end">
                      {block === undefined && (
                        <Button variant="secondary" disabled={busy || !hasReason} onClick={() => void decide(p, 'APPROVE')}>{t('pay.approve')}</Button>
                      )}
                      {payoutRejectable(p, mayDecide) && (
                        <Button variant="danger" disabled={busy || !hasReason} onClick={() => void decide(p, 'REJECT')}>{own ? t('payouts.withdraw') : t('pay.reject')}</Button>
                      )}
                    </Row>
                    {block !== undefined && block !== 'NOT_PENDING' && (
                      <NoteText compact>{v('finance.payout_decision_block', block)}</NoteText>
                    )}
                  </Stack>
                );
              },
            },
          ]}
          rows={rows ?? []}
          rowKey={(p) => p.payoutId}
          loading={rows === undefined}
          loadingLabel={t('ui.loading')}
          empty={<EmptyState title={t('pay.queue.empty')} />}
        />
      </Panel>
      </>
      )}

      <Panel>
        <Stack gap={3}>
        <SectionTitle>{t('payouts.issue.title')}</SectionTitle>
        <Text as="p" tone="secondary" block>{t('payouts.issue.hint')}</Text>
        {!mayIssue ? (
          <Notice tone="neutral">{t('payouts.issue.noPermission')}</Notice>
        ) : (
          <form onSubmit={(e) => void onIssue(e)} noValidate>
            <Stack gap={3}>
            <FormRow
              action={
                <Button variant="secondary" disabled={checking || driverId.trim() === ''} onClick={() => void checkDriver()}>{t('payouts.issue.check')}</Button>
              }
            >
              <Field label={t('payouts.issue.driver')} htmlFor="payout-driver">
                <TextInput
                  id="payout-driver"
                  className="tnum"
                  value={driverId}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => {
                    setDriverId(e.target.value);
                    setContext(undefined);
                    setAmount(undefined);
                  }}
                />
              </Field>
            </FormRow>
            {context !== undefined && context.currency !== null && (
              <Stack gap={2}>
                <Notice tone="neutral">{t('payouts.issue.context', { market: context.marketId !== null ? marketDisplayName(context.marketId, markets) : '—', currency: context.currency })}</Notice>
                {/* A2 (D392): what the ledger owes this person — the cap the server will apply, shown before an amount is typed. */}
                {context.owed !== undefined && context.owed !== null ? (
                  <Notice tone="neutral">
                    {t('payouts.owed.available', {})}: {money({ amountMinor: context.owed.availableMinor, currency: context.owed.currency })} — {owedSummary(context.owed)}
                  </Notice>
                ) : (
                  <Notice tone="warning">{t('payouts.owed.unknown')}</Notice>
                )}
              </Stack>
            )}
            {context !== undefined && context.currency === null && <Notice tone="warning">{t('payouts.issue.noCurrency')}</Notice>}
            {context !== undefined && context.currency !== null && (
              <MoneyInput
                id="payout-amount"
                label={t('payouts.issue.amount')}
                currency={context.currency}
                locale={lang}
                valueMinor={amount}
                onChangeMinor={setAmount}
                issueText={(issue) => t(`money.issue.${issue}`)}
                required
              />
            )}
            {overCap && context?.owed !== undefined && context.owed !== null && (
              <Notice tone="warning">{t('payouts.owed.overCap', { available: money({ amountMinor: context.owed.availableMinor, currency: context.owed.currency }) })}</Notice>
            )}
            {issueMsg !== undefined && <Notice tone={issueMsg.tone}>{issueMsg.text}</Notice>}
            <Button type="submit" variant="primary" disabled={issueBusy || overCap || context === undefined || context.currency === null || amount === undefined || amount <= 0n}>
              {issueBusy ? t('payouts.issue.busy') : t('payouts.issue.submit')}
            </Button>
            </Stack>
          </form>
        )}
        </Stack>
      </Panel>
    </>
  );
}
